import type { MenuItemsType } from '@duke-hq/ui/components/SubSidebar/MenuItems';
import { SidebarLayout } from '@duke-hq/ui/components/SubSidebar/SidebarLayout';
import { constructQueryParams, stringifyValues } from '@duke-hq/lib';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { t } from '@lingui/core/macro';
import { Archive, UserPlus, Users, Zap } from 'lucide-react';
import { useMemo } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import { useTenantContext } from '@duke-hq/app-provider-web/tenant/TenantContext';
import { StudentCourseEnrolmentStatus, StudentActivationStatus } from '@duke-hq/svc-api-db';
import FiltersProvider from '@duke-hq/filters/context/FiltersProvider';
import { parseSortOrder, SortOrder } from '@duke-hq/lib/pagination';

export interface ProviderEnrolmentsURLParams {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
  page?: string | undefined;
  search?: string | undefined;
  status?: string | undefined;
  course?: string | undefined;
  health?: string | undefined;
  progress?: string | undefined;
  siteLocationOrRegionIds?: string | undefined;
  siteLocationIds?: string[] | undefined;
  siteRegionIds?: string[] | undefined;

  name?: string | undefined;
  unitLag?: string | undefined;
  createdAt?: string | undefined;
  lastLogin?: string | undefined;
}

export function EnrolmentsLayout() {
  const [searchParams] = useSearchParams();
  const tenantCtx = useTenantContext();
  const isTenantUsingNewReferralProcess = tenantCtx?.settings.uses_new_referral_process || false;

  const { data: referralsCount = {}, isLoading: isLoadingReferralPending } =
    trpcTenant.courseReferral.countByStatus.useQuery({});

  const hasPendingOrAvailableReferrals =
    !isLoadingReferralPending &&
    ((referralsCount[StudentActivationStatus.PENDING] ?? 0) > 0 ||
      (referralsCount[StudentActivationStatus.AVAILABLE] ?? 0) > 0);

  function generateMenuItems(
    urlParams: ProviderEnrolmentsURLParams,
    enrolmentsReport,
  ): MenuItemsType[] {
    const baseUrl = '/enrolments';
    const activeEnrolmentProgress =
      urlParams.progress && urlParams.progress !== 'NOT_STARTED' ? urlParams.progress : undefined;
    const { siteLocationIds, siteRegionIds, ...stringifiableParams } = urlParams;
    const stringifiedParams = stringifyValues(stringifiableParams);
    const menuItems = [
      {
        alias: 'Active',
        label: t`Active enrolments`,
        link: `${baseUrl}${constructQueryParams({
          ...stringifiedParams,
          status: 'IN_PROGRESS',
          progress: activeEnrolmentProgress,
        })}`,
        icon: Zap,
        snapshot: enrolmentsReport ? enrolmentsReport['active_enrolments'] : 0,
      },
      {
        label: t`All`,
        link: `${baseUrl}${constructQueryParams({ ...stringifiedParams, status: undefined })}`,
        icon: Users,
        snapshot: enrolmentsReport ? enrolmentsReport['total_enrolments'] : 0,
      },
      {
        alias: 'Past',
        label: t`Past enrolments`,
        link: `${baseUrl}${constructQueryParams({
          ...stringifiedParams,
          status: `${StudentCourseEnrolmentStatus.COMPLETED},${StudentCourseEnrolmentStatus.WITHDRAWN}`,
          progress: activeEnrolmentProgress,
        })}`,
        icon: Archive,
        snapshot: enrolmentsReport
          ? enrolmentsReport['completed_enrolments'] + enrolmentsReport['withdrawn_enrolments']
          : 0,
        showDivider: !!hasPendingOrAvailableReferrals || !!isTenantUsingNewReferralProcess,
      },
    ];

    if (hasPendingOrAvailableReferrals || isTenantUsingNewReferralProcess) {
      menuItems.push({
        alias: 'Pending',
        label: t`Pending referrals`,
        link: `${baseUrl}/referrals${constructQueryParams({
          ...stringifiedParams,
          status: StudentActivationStatus.PENDING,
        })}`,
        icon: UserPlus,
        snapshot: enrolmentsReport ? enrolmentsReport['pending_referrals'] : 0,
      });
    }

    return menuItems;
  }

  const getExistingUrlParams: ProviderEnrolmentsURLParams = useMemo(() => {
    const { sort, ...rest } = Object.fromEntries(searchParams.entries());
    return {
      ...rest,
      orderBy: rest.orderBy,
      sort: sort ? parseSortOrder(sort) : undefined,
    };
  }, [searchParams]);

  const { data: enrolmentsReport } = trpcTenant.enrolments.enrolmentsReport.useQuery({
    excludeInternalEnrolments: true,
  });

  if (enrolmentsReport) {
    enrolmentsReport['pending_referrals'] = referralsCount
      ? (referralsCount[StudentActivationStatus.AVAILABLE] ?? 0) +
        (referralsCount[StudentActivationStatus.PENDING] ?? 0) // AVAILABLE and PENDING statuses indicate referrals that are not yet consumed
      : 0;
  }

  const sideDrawerItems = generateMenuItems(getExistingUrlParams, enrolmentsReport);

  return (
    <FiltersProvider localStorageKey="enrolments_page_filters">
      <SidebarLayout items={sideDrawerItems} label={t`Enrolments`}>
        <Outlet />
      </SidebarLayout>
    </FiltersProvider>
  );
}
